import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';

export interface UserReview {
  id: string;
  transaction_id: string;
  reviewer_id: string;
  reviewed_user_id: string;
  rating: number;
  comment: string | null;
  tags: string[];
  created_at: string;
  reviewer: {
    username: string;
    avatar_url: string | null;
  } | null;
}

export const REVIEW_TAGS = [
  { id: 'condition_accurate', label: 'Condition Accurate', emoji: '✓' },
  { id: 'no_flake', label: 'No Flake', emoji: '⏰' },
  { id: 'fast_shipper', label: 'Fast Shipper', emoji: '📦' },
  { id: 'good_communication', label: 'Good Communication', emoji: '💬' },
  { id: 'fair_pricing', label: 'Fair Pricing', emoji: '💰' },
] as const;

export type ReviewTagId = typeof REVIEW_TAGS[number]['id'];

// Get reviews for a specific user
export function useUserReviews(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reviews.byUser(userId),
    queryFn: async () => {
      if (!userId) return [];

      // Get reviews
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, transaction_id, reviewer_id, reviewed_user_id, rating, comment, tags, created_at')
        .eq('reviewed_user_id', userId)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;
      if (!reviews || reviews.length === 0) return [];

      // Get reviewer profiles separately
      const reviewerIds = [...new Set(reviews.map(r => r.reviewer_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('public_profiles')
        .select('user_id, username, avatar_url')
        .in('user_id', reviewerIds);

      if (profilesError) throw profilesError;

      // Combine data
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      
      return reviews.map(review => ({
        ...review,
        tags: review.tags || [],
        reviewer: profileMap.get(review.reviewer_id) || null,
      })) as UserReview[];
    },
    enabled: !!userId,
  });
}

// Get review tag counts for a user
export function useReviewTagCounts(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reviews.tagCounts(userId),
    queryFn: async () => {
      if (!userId) return {};

      const { data, error } = await supabase
        .from('reviews')
        .select('tags')
        .eq('reviewed_user_id', userId);

      if (error) throw error;

      // Count tags
      const counts: Record<string, number> = {};
      for (const review of data || []) {
        for (const tag of review.tags || []) {
          counts[tag] = (counts[tag] || 0) + 1;
        }
      }
      return counts;
    },
    enabled: !!userId,
  });
}

// Weighted rating: reviews from last 90 days count 2x
export function useWeightedRating(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reviews.weighted(userId),
    queryFn: async () => {
      if (!userId) return null;

      const { data: reviews, error } = await supabase
        .from('reviews')
        .select('rating, created_at')
        .eq('reviewed_user_id', userId);

      if (error) throw error;
      if (!reviews || reviews.length === 0) return null;

      const now = new Date();
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      
      let totalWeight = 0;
      let weightedSum = 0;
      let recentReviews = 0;

      for (const review of reviews) {
        const createdAt = new Date(review.created_at);
        const isRecent = createdAt >= ninetyDaysAgo;
        const weight = isRecent ? 2 : 1;
        
        weightedSum += review.rating * weight;
        totalWeight += weight;
        
        if (isRecent) recentReviews++;
      }

      const weightedRating = totalWeight > 0 ? weightedSum / totalWeight : 0;

      return {
        weightedRating: Math.round(weightedRating * 10) / 10, // Round to 1 decimal
        totalReviews: reviews.length,
        recentReviews,
        averageRating: reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length,
      };
    },
    enabled: !!userId,
  });
}
